import type { ProcessedFormField } from "@/types/api/formSchema";

export const SECTION_ORDER = [
  "top",
  "ADDRESS",
  "BANK DETAILS",
  "CONTACT & NOTIFICATION",
  "PORTAL ACCESS",
  "OTHER",
] as const;

export type SectionKey = (typeof SECTION_ORDER)[number];

export const SECTION_TITLES: Record<SectionKey, string> = {
  top: "",
  ADDRESS: "Address",
  "BANK DETAILS": "Bank Details",
  "CONTACT & NOTIFICATION": "Contact & Notification",
  "PORTAL ACCESS": "Portal Access",
  OTHER: "Other",
};

const SECTION_MAP: Record<string, SectionKey> = {
  // Facility identity
  facility_name: "top",
  // Row 1: GST Number, State (from GSTIN)
  gstin_primary: "top",
  state_of_registration: "top",
  // Row 2: Branch Email, Branch Phone
  facility_branch_email: "top",
  facility_branch_phone: "top",
  // Only when International — SWIFT / BIC in top strip
  swift_bic: "top",
  facility_gst_certificate_attachment: "top",
  facility_gst_declaration_attachment: "top",
  // International-only compliance documents — collected at facility level
  tax_residency_certificate_attachment: "top",
  no_pe_declaration_attachment: "top",

  facility_address_line_1: "ADDRESS",
  facility_address_line_2: "ADDRESS",
  facility_pincode: "ADDRESS",
  facility_city: "ADDRESS",
  facility_country: "ADDRESS",

  facility_ifsc_code: "BANK DETAILS",
  facility_account_number: "BANK DETAILS",
  facility_account_number_confirm: "BANK DETAILS",
  account_holder_name: "BANK DETAILS",
  facility_bank_name: "BANK DETAILS",
  facility_bank_branch: "BANK DETAILS",
  facility_account_type: "BANK DETAILS",
  cancelled_cheque_attachment: "BANK DETAILS",
  // International-only bank fields — IBAN + bank-details letter attachment
  iban: "BANK DETAILS",
  bank_details_letter_attachment: "BANK DETAILS",

  poc_name: "CONTACT & NOTIFICATION",
  poc_mobile_number: "CONTACT & NOTIFICATION",
  poc_designation: "CONTACT & NOTIFICATION",
  contact_email: "CONTACT & NOTIFICATION",
  po_notification_emails: "CONTACT & NOTIFICATION",
  escalation_emails: "CONTACT & NOTIFICATION",

  vendor_invoice_portal_access_email: "PORTAL ACCESS",
  tars_production_process_management_access_email: "PORTAL ACCESS",
};

/** Fields rendered at 1/3 width (three per row). */
export const THIRD_WIDTH_NAMES = new Set([
  "facility_pincode",
  "facility_city",
  "facility_country",
]);

/**
 * Fields that render outside the branch card's field grid — surfaced in the
 * card header instead (Primary Facility radio).
 */
export const HEADER_FIELDS = new Set(["is_primary_facility"]);

/** Fields to always render at 12/12 width even if their type is not full-width. */
export const FULL_WIDTH_NAMES = new Set([
  "facility_address_line_1",
  "facility_address_line_2",
  "po_notification_emails",
  "escalation_emails",
  "vendor_invoice_portal_access_email",
  "tars_production_process_management_access_email",
]);

export function sectionFor(fieldName: string): SectionKey {
  return SECTION_MAP[fieldName] ?? "OTHER";
}

export type BranchFieldGroups = Record<SectionKey, ProcessedFormField[]>;

export function groupBranchFields(
  fields: ProcessedFormField[],
): BranchFieldGroups {
  const groups: BranchFieldGroups = {
    top: [],
    ADDRESS: [],
    "BANK DETAILS": [],
    "CONTACT & NOTIFICATION": [],
    "PORTAL ACCESS": [],
    OTHER: [],
  };
  for (const f of fields) {
    if (HEADER_FIELDS.has(f.name)) continue;
    groups[sectionFor(f.name)].push(f);
  }
  return groups;
}
